/*
  Cart vehicle check.

  Remembers which vehicle an adapter was matched to and shows it above that
  adapter in the cart drawer ("Matched for: Your 2025 Toyota Tacoma"). The
  "Not my car?" link opens a Make / Model / Year pop-up that re-checks the fit
  against the compatibility-api (read only) and offers a swap when it doesn't.

  Works with every Fit Finder version without touching them:
  - Fit Finder (control, v2, v3) fires `dcf:search` and the AI assistant fires
    `dgr:assistant` {type: 'fitment_shown'}, both with make/model/year and the
    matched SKUs. That search is kept for an hour (localStorage
    `dongar_cv_last_search`), so any /cart/add of one of those SKUs, on the
    finder or on a product page clicked through to, is remembered against the
    vehicle.
  - The assistant's own add ({type: 'add_to_cart', sku, make, model, year}) is
    remembered directly.

  Stored per browser in localStorage (`dongar_cart_vehicles`, SKU → vehicle).
  Nothing is written to the cart or order. Loaded in layout/theme.liquid
  before preorder-ack.js / cable-check.js, so their fetch wrappers sit on top
  of this one.
*/
(function () {
  if (window.__dongarCartVehicle) return;
  window.__dongarCartVehicle = true;

  var BASE = 'https://vdfgvxqwcjdupvtjhnmy.supabase.co/functions/v1/compatibility-api';
  var ANON = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZkZmd2eHF3Y2pkdXB2dGpobm15Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzMzNjEwNzUsImV4cCI6MjA4ODkzNzA3NX0.EKFuutkb7vGysPMA3XsVuyuMbHCTalCVNETQqSZQEkE';
  var STORE = 'dongar_cart_vehicles';
  var MAX_AGE = 30 * 24 * 60 * 60 * 1000;
  var SEARCH_WINDOW = 60 * 60 * 1000;
  var FINDER_URL = '/pages/fit-finder';
  var LOCATIONS = {
    'mirror-back': 'Rearview mirror',
    'mirror-shroud': 'Rearview mirror',
    'domelight': 'Dome light',
    'rainsensor': 'Rain sensor',
    'tss': 'Safety Sense module',
    'combo': 'Parking-mode kit'
  };

  var nativeFetch = window.fetch.bind(window);

  // ---------- helpers ----------

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function api(params) {
    var u = new URL(BASE);
    Object.keys(params).forEach(function (k) {
      if (params[k] != null && params[k] !== '') u.searchParams.set(k, String(params[k]));
    });
    return nativeFetch(u.toString(), { headers: { apikey: ANON, Authorization: 'Bearer ' + ANON } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
  }

  function vehicleName(v) {
    return [v.year, v.make, v.model, v.trim].filter(Boolean).join(' ');
  }

  function money(cents) {
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function track(type, detail) {
    try { window.dispatchEvent(new CustomEvent('dongar:cart-vehicle', { detail: Object.assign({ type: type }, detail || {}) })); } catch (e) {}
  }

  // ---------- remembered vehicles ----------

  function load() {
    try {
      var m = JSON.parse(window.localStorage.getItem(STORE) || '{}') || {};
      var now = Date.now();
      Object.keys(m).forEach(function (k) { if (!m[k] || now - (m[k].ts || 0) > MAX_AGE) delete m[k]; });
      return m;
    } catch (e) {
      return {};
    }
  }

  function remember(sku, v) {
    if (!sku || !v || !v.make || !v.model) return;
    var m = load();
    m[sku] = { make: v.make, model: v.model, year: v.year ? String(v.year) : '', trim: v.trim || '', ts: Date.now() };
    try { window.localStorage.setItem(STORE, JSON.stringify(m)); } catch (e) {}
  }

  // The last vehicle searched, kept across pages so an add from the product
  // page the shopper clicked through to still counts.
  var SEARCH_KEY = 'dongar_cv_last_search';

  function setSearch(d) {
    if (!d.make || !d.model || !d.skus || !d.skus.length) return;
    var s = { make: d.make, model: d.model, year: d.year, skus: d.skus, ts: Date.now() };
    try { window.localStorage.setItem(SEARCH_KEY, JSON.stringify(s)); } catch (e) {}
  }

  function getSearch() {
    try {
      var s = JSON.parse(window.localStorage.getItem(SEARCH_KEY) || 'null');
      return s && Date.now() - (s.ts || 0) < SEARCH_WINDOW ? s : null;
    } catch (e) {
      return null;
    }
  }

  window.addEventListener('dcf:search', function (e) {
    setSearch((e && e.detail) || {});
  });

  window.addEventListener('dgr:assistant', function (e) {
    var d = (e && e.detail) || {};
    if (d.type === 'fitment_shown') { setSearch(d); return; }
    if (d.type !== 'add_to_cart' || !d.sku || !d.make || !d.model) return;
    remember(d.sku, d);
    scheduleApply();
  });

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var p = nativeFetch(input, init);
    if (!/\/cart\/add(\.js)?(\?|$)/.test(url)) return p;
    return p.then(function (res) {
      var search = res.ok && getSearch();
      if (search) {
        res.clone().json().then(function (d) {
          var items = (d && d.items) || (d && d.sku !== undefined ? [d] : []);
          items.forEach(function (i) {
            if (i.sku && search.skus.indexOf(i.sku) !== -1) remember(i.sku, search);
          });
          scheduleApply();
        }).catch(function () {});
      }
      return res;
    });
  };

  // ---------- drawer label ----------

  var CAR_ICON = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 15v-3l2-5h11l3 5h2v3"/><path d="M3 15h18"/><circle cx="7" cy="16.5" r="1.8"/><circle cx="17" cy="16.5" r="1.8"/></svg>';

  function apply() {
    var map = load();
    document.querySelectorAll('#CartDrawer tr.cart-item[data-sku]').forEach(function (row) {
      var v = map[row.getAttribute('data-sku')];
      var prev = row.previousElementSibling;
      var existing = prev && prev.classList.contains('cv-row') ? prev : null;
      if (!v) { if (existing) existing.remove(); return; }
      var name = vehicleName(v);
      if (existing && existing.getAttribute('data-v') === name) return;
      if (existing) existing.remove();
      var tr = document.createElement('tr');
      tr.className = 'cv-row';
      tr.setAttribute('data-v', name);
      tr.innerHTML =
        '<td colspan="4"><div class="cv-chip">' + CAR_ICON +
          '<div class="cv-chip__text"><span class="cv-chip__eyebrow">Matched for</span>' +
          '<span class="cv-chip__car">Your ' + esc(name) + '</span></div>' +
          '<button type="button" class="cv-chip__link" data-cv-open>Not my car?</button>' +
        '</div></td>';
      row.parentNode.insertBefore(tr, row);
    });
  }

  var applyTimer = null;
  function scheduleApply() {
    clearTimeout(applyTimer);
    applyTimer = setTimeout(apply, 30);
  }

  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    new MutationObserver(scheduleApply).observe(drawer, { childList: true, subtree: true });
    apply();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchDrawer);
  else watchDrawer();

  // ---------- pop-up ----------

  var dlg = null;
  var ctx = null; // { sku, key, qty, current, vehicle, rows, trims, products, locations, trim }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-cv-open]');
    if (!btn) return;
    var row = btn.closest('tr.cv-row');
    var item = row && row.nextElementSibling;
    if (!item || !item.hasAttribute('data-sku')) return;
    e.preventDefault();
    open({
      sku: item.getAttribute('data-sku'),
      key: item.getAttribute('data-key'),
      qty: parseInt(item.getAttribute('data-qty'), 10) || 1,
      current: load()[item.getAttribute('data-sku')] || null,
      opener: btn
    });
  });

  function ensureDialog() {
    if (dlg) return dlg;
    dlg = document.createElement('dialog');
    dlg.className = 'cv-dialog';
    dlg.setAttribute('aria-labelledby', 'cv-title');
    document.body.appendChild(dlg);
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg) { close(); return; }
      var a = e.target.closest('[data-cv]');
      if (!a) return;
      var act = a.getAttribute('data-cv');
      if (act === 'close') close();
      else if (act === 'check') check();
      else if (act === 'retry') renderForm();
      else if (act === 'trim') pickTrim(a.getAttribute('data-trim'));
      else if (act === 'unsure') renderGrouped();
      else if (act === 'swap') swap(a.getAttribute('data-sku'), a);
    });
    dlg.addEventListener('close', function () {
      if (ctx && ctx.opener && document.contains(ctx.opener)) ctx.opener.focus();
    });
    return dlg;
  }

  function open(c) {
    ctx = c;
    ensureDialog();
    renderForm();
    if (!dlg.open) dlg.showModal();
    track('open', { sku: c.sku });
  }

  function close() {
    if (dlg && dlg.open) dlg.close();
  }

  function render(html) {
    dlg.innerHTML = '<div class="cv-card">' + html + '</div>';
    var focus = dlg.querySelector('[autofocus], select, button.cv-btn, a.cv-btn');
    if (focus) focus.focus();
  }

  var CLOSE_BTN = '<button type="button" class="cv-x" data-cv="close" aria-label="Close"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg></button>';
  var ALERT_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5M12 16.5v.5"/></svg>';

  function adapterName() {
    var p = ctx.products && ctx.products[ctx.sku];
    return p && p.shortName ? p.shortName + ' (' + ctx.sku + ')' : 'adapter (' + ctx.sku + ')';
  }

  function keepLink() {
    var label = ctx.current ? 'Cancel, keep ' + vehicleName(ctx.current) : 'Cancel';
    return '<button type="button" class="cv-link cv-center" data-cv="close">' + esc(label) + '</button>';
  }

  // Step 1: Make / Model / Year
  function renderForm() {
    ctx.vehicle = null;
    render(
      '<div class="cv-head"><div class="cv-head__text">' +
        '<h2 id="cv-title" class="cv-title">Double-check your vehicle</h2>' +
        '<p class="cv-sub">We\'ll check that the ' + esc(adapterName()) + ' fits.</p>' +
      '</div>' + CLOSE_BTN + '</div>' +
      '<div class="cv-fields">' +
        field('cv-make', 'Make', 'Select make') +
        field('cv-model', 'Model', 'Select model') +
        field('cv-year', 'Year', 'Select year') +
      '</div>' +
      '<p class="cv-error" data-cv-error hidden></p>' +
      '<button type="button" class="cv-btn" data-cv="check" disabled>Check fit</button>' +
      keepLink()
    );
    var make = dlg.querySelector('#cv-make');
    var model = dlg.querySelector('#cv-model');
    var year = dlg.querySelector('#cv-year');
    var btn = dlg.querySelector('[data-cv="check"]');
    model.disabled = true; year.disabled = true;

    function gate() { btn.disabled = !(make.value && model.value && year.value); }

    fill(make, null, 'Loading…');
    api({ action: 'makes' }).then(function (d) {
      fill(make, (d.makes || []).slice().sort(), 'Select make');
    }).catch(function () { fill(make, null, 'Couldn\'t load makes'); });

    make.addEventListener('change', function () {
      fill(model, null, 'Select model'); model.disabled = true;
      fill(year, null, 'Select year'); year.disabled = true;
      gate();
      if (!make.value) return;
      fill(model, null, 'Loading…');
      api({ action: 'models', make: make.value }).then(function (d) {
        fill(model, (d.models || []).slice().sort(), 'Select model');
        model.disabled = false;
      }).catch(function () { fill(model, null, 'Couldn\'t load models'); });
    });

    model.addEventListener('change', function () {
      fill(year, null, 'Select year'); year.disabled = true;
      gate();
      if (!model.value) return;
      fill(year, null, 'Loading…');
      api({ action: 'years', make: make.value, model: model.value }).then(function (d) {
        var ys = (d.years || []).map(String).sort().reverse();
        fill(year, ys, 'Select year');
        year.disabled = false;
      }).catch(function () { fill(year, null, 'Couldn\'t load years'); });
    });

    year.addEventListener('change', gate);
  }

  function field(id, label, placeholder) {
    return '<div class="cv-field"><label for="' + id + '">' + label + '</label>' +
      '<select id="' + id + '"><option value="">' + esc(placeholder) + '</option></select></div>';
  }

  function fill(sel, values, placeholder) {
    sel.innerHTML = '<option value="">' + esc(placeholder) + '</option>' +
      (values || []).map(function (v) { return '<option value="' + esc(v) + '">' + esc(v) + '</option>'; }).join('');
  }

  // Step 2: search, then route to fits / trim / swap
  function check() {
    var v = {
      make: dlg.querySelector('#cv-make').value,
      model: dlg.querySelector('#cv-model').value,
      year: dlg.querySelector('#cv-year').value
    };
    if (!v.make || !v.model || !v.year) return;
    ctx.vehicle = v;
    ctx.trim = '';
    render('<div class="cv-loading" role="status">Checking your ' + esc(vehicleName(v)) + '…</div>');
    track('check', { sku: ctx.sku, make: v.make, model: v.model, year: v.year });

    api({ action: 'search', make: v.make, model: v.model, year: v.year }).then(function (d) {
      var rows = d.data || [];
      ctx.trims = {};
      var all = {};
      rows.forEach(function (r) {
        var t = [r.submodel1, r.submodel2].filter(Boolean).join(' ').trim();
        (ctx.trims[t] = ctx.trims[t] || {})[r.sku] = true;
        all[r.sku] = true;
      });
      ctx.allSkus = Object.keys(all);
      if (!ctx.allSkus.length) { renderNone(); return null; }
      return loadProducts(ctx.allSkus.concat([ctx.sku])).then(route);
    }).catch(renderError);
  }

  function namedTrims() {
    return Object.keys(ctx.trims).filter(Boolean).sort();
  }

  // Every SKU the database lists for a trim (rows with no trim apply to all).
  function fitsFor(trim) {
    return Object.keys(Object.assign({}, ctx.trims[''] || {}, trim ? ctx.trims[trim] || {} : {}));
  }

  // The same list, limited to adapters we can show and sell.
  function skusFor(trim) {
    return fitsFor(trim).filter(function (k) { return ctx.products[k]; });
  }

  function route() {
    var trims = namedTrims();
    var sets = trims.map(function (t) { return skusFor(t).sort().join(','); });
    var differ = sets.some(function (s) { return s !== sets[0]; });
    var fitsAll = trims.length
      ? trims.every(function (t) { return fitsFor(t).indexOf(ctx.sku) !== -1; })
      : fitsFor('').indexOf(ctx.sku) !== -1;

    if (fitsAll) { renderFits(''); return; }
    if (!differ) {
      var skus = trims.length ? skusFor(trims[0]) : skusFor('');
      if (!skus.length) { renderNone(); return; }
      renderSwap(skus, '');
      return;
    }
    renderTrimAsk('');
  }

  function loadProducts(skus) {
    ctx.products = ctx.products || {};
    ctx.locations = ctx.locations || {};
    var need = skus.filter(function (s, i) { return skus.indexOf(s) === i && !ctx.products[s]; });
    if (!need.length) return Promise.resolve();
    return Promise.all([
      api({ action: 'products', skus: need.join(',') }),
      api({ action: 'sku_reference', skus: need.join(',') }).catch(function () { return {}; })
    ]).then(function (res) {
      (res[1].references || []).forEach(function (r) { if (r.sku) ctx.locations[r.sku] = LOCATIONS[r.install_location] || ''; });
      var byHandle = {};
      (res[0].products || []).forEach(function (p) {
        if (p.product_type && p.product_type !== 'adapter') return;
        if (!p.shopify_variant_id || !p.shopify_handle) return;
        (byHandle[p.shopify_handle] = byHandle[p.shopify_handle] || []).push(p);
      });
      return Promise.all(Object.keys(byHandle).map(function (handle) {
        return nativeFetch('/products/' + encodeURIComponent(handle) + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; })
          .then(function (sp) {
            byHandle[handle].forEach(function (p) {
              var variant = sp && (sp.variants || []).filter(function (x) { return String(x.id) === String(p.shopify_variant_id); })[0];
              if (!variant) return;
              ctx.products[p.sku] = {
                sku: p.sku,
                variantId: variant.id,
                shortName: sp.title,
                price: variant.price,
                available: variant.available,
                image: p.image_url || (sp.featured_image || '')
              };
            });
          });
      }));
    });
  }

  function img(p) {
    if (!p.image) return '<div class="cv-opt__img" aria-hidden="true"></div>';
    var src = p.image.indexOf('cdn.shopify.com') !== -1 ? p.image + (p.image.indexOf('?') === -1 ? '?' : '&') + 'width=120' : p.image;
    return '<img class="cv-opt__img" src="' + esc(src) + '" alt="" loading="lazy" width="56" height="56">';
  }

  function option(sku, first) {
    var p = ctx.products[sku];
    if (!p) return '';
    var loc = ctx.locations[sku];
    var inCart = sku === ctx.sku;
    var action = inCart
      ? '<span class="cv-opt__incart">In your cart</span>'
      : p.available
        ? '<button type="button" class="cv-swap' + (first ? ' cv-swap--primary' : '') + '" data-cv="swap" data-sku="' + esc(sku) + '" aria-label="Swap to ' + esc(p.shortName + ' ' + sku) + '">Swap</button>'
        : '<span class="cv-opt__incart">Sold out</span>';
    return '<div class="cv-opt' + (first && !inCart ? ' cv-opt--primary' : '') + '">' + img(p) +
      '<div class="cv-opt__info">' +
        (loc ? '<span class="cv-tag' + (first ? ' cv-tag--lime' : '') + '">' + esc(loc) + '</span>' : '') +
        '<span class="cv-opt__name">' + esc(p.shortName) + '</span>' +
        '<span class="cv-opt__meta">' + esc(sku) + ' · ' + esc(money(p.price)) + '</span>' +
      '</div>' + action + '</div>';
  }

  function sortSkus(skus) {
    // Available first, then cheapest, so the first card is the simplest swap.
    return skus.slice().sort(function (a, b) {
      var pa = ctx.products[a], pb = ctx.products[b];
      if (pa.available !== pb.available) return pa.available ? -1 : 1;
      return pa.price - pb.price;
    });
  }

  function options(skus) {
    var firstDone = false;
    return '<div class="cv-opts">' + sortSkus(skus).map(function (s) {
      var html = option(s, !firstDone && ctx.products[s].available);
      if (ctx.products[s].available) firstDone = true;
      return html;
    }).join('') + '</div>';
  }

  function footer() {
    return '<div class="cv-foot">' +
      '<button type="button" class="cv-link" data-cv="retry">Try a different vehicle</button>' +
      '<button type="button" class="cv-link cv-link--muted" data-cv="close">Keep my cart</button>' +
    '</div>';
  }

  function finderLink(text) {
    var v = ctx.vehicle || {};
    var u = FINDER_URL + '?' + ['make', 'model', 'year'].map(function (k) {
      return k + '=' + encodeURIComponent(v[k] || '');
    }).join('&');
    return '<a href="' + esc(u) + '">' + esc(text) + '</a>';
  }

  function eyebrow(text) {
    return '<div class="cv-eyebrow">' + ALERT_ICON + esc(text) + '</div>';
  }

  // Result: it fits
  function renderFits(trim) {
    var v = Object.assign({}, ctx.vehicle, { trim: trim || '' });
    remember(ctx.sku, v);
    scheduleApply();
    track('fits', { sku: ctx.sku, make: v.make, model: v.model, year: v.year, trim: v.trim });
    render(
      '<div class="cv-center-col">' +
        '<div class="cv-ok" aria-hidden="true"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></div>' +
        '<h2 id="cv-title" class="cv-title">Good news, it fits</h2>' +
        '<p class="cv-sub">The ' + esc(adapterName()) + ' fits your <strong>' + esc(vehicleName(v)) + '</strong>. No changes needed.</p>' +
        '<button type="button" class="cv-btn" data-cv="close" autofocus>Back to cart</button>' +
      '</div>'
    );
  }

  // Result: doesn't fit, same adapters for every trim
  function renderSwap(skus, trim) {
    var v = Object.assign({}, ctx.vehicle, { trim: trim || '' });
    track('no_fit', { sku: ctx.sku, make: v.make, model: v.model, year: v.year, trim: v.trim });
    render(
      '<div class="cv-head"><div class="cv-head__text">' + eyebrow('Not a match') +
        '<h2 id="cv-title" class="cv-title">This adapter won\'t fit your ' + esc(vehicleName(v)) + '</h2>' +
        '<p class="cv-sub">The ' + esc(adapterName()) + ' in your cart is for a different plug. These fit your ' + esc(v.model) + ':</p>' +
      '</div>' + CLOSE_BTN + '</div>' +
      options(skus) +
      '<p class="cv-note">Not sure where your dash cam goes? ' + finderLink('Open the full Fit Finder') + ' for install photos and trims.</p>' +
      footer()
    );
  }

  // Result: adapters differ by trim, ask first (3d-1 / 3d-2)
  function renderTrimAsk(selected) {
    var v = ctx.vehicle;
    var bubbles = namedTrims().map(function (t) {
      var on = t === selected;
      return '<button type="button" class="cv-bubble' + (on ? ' is-on' : '') + '" role="radio" aria-checked="' + on + '" data-cv="trim" data-trim="' + esc(t) + '">' + esc(t) + '</button>';
    }).join('') +
      '<button type="button" class="cv-bubble cv-bubble--unsure" data-cv="unsure">I\'m not sure</button>';

    var results = '';
    if (selected) {
      var picked = skusFor(selected);
      results = '<div class="cv-results"><span class="cv-results__label">Fits your ' + esc(v.model + ' ' + selected) + ':</span>' +
        (picked.length ? options(picked) : '<p class="cv-note">No Dongar adapter for this trim yet. ' + finderLink('Open the full Fit Finder') + ' for other options.</p>') +
        '</div>';
    }

    render(
      '<div class="cv-head"><div class="cv-head__text">' + eyebrow('One more step') +
        '<h2 id="cv-title" class="cv-title">Which ' + esc(vehicleName(v)) + ' trim do you have?</h2>' +
        '<p class="cv-sub">The right adapter depends on your trim.</p>' +
      '</div>' + CLOSE_BTN + '</div>' +
      '<div class="cv-bubbles" role="radiogroup" aria-label="Trim">' + bubbles + '</div>' +
      results +
      '<p class="cv-note">Trim is usually on the tailgate badge or your registration.</p>' +
      footer()
    );
    if (selected) {
      var on = dlg.querySelector('.cv-bubble.is-on');
      if (on) on.focus();
    }
  }

  function pickTrim(trim) {
    ctx.trim = trim;
    track('trim', { sku: ctx.sku, trim: trim });
    if (fitsFor(trim).indexOf(ctx.sku) !== -1) { renderFits(trim); return; }
    renderTrimAsk(trim);
  }

  // "I'm not sure": every trim group with its adapters (3c)
  function renderGrouped() {
    var v = ctx.vehicle;
    var groups = {};
    var order = [];
    namedTrims().forEach(function (t) {
      var key = skusFor(t).sort().join(',');
      if (!groups[key]) { groups[key] = []; order.push(key); }
      groups[key].push(t);
    });
    track('unsure', { sku: ctx.sku });
    var html = order.map(function (key) {
      var skus = key ? key.split(',') : [];
      return '<div class="cv-group"><span class="cv-results__label">' + esc(groups[key].join(' · ')) + '</span>' +
        (skus.length ? options(skus) : '<p class="cv-note">No Dongar adapter for this trim yet.</p>') + '</div>';
    }).join('');
    render(
      '<div class="cv-head"><div class="cv-head__text">' + eyebrow('Depends on your trim') +
        '<h2 id="cv-title" class="cv-title">Your ' + esc(vehicleName(v)) + ' uses different adapters by trim</h2>' +
        '<p class="cv-sub">Find your trim below:</p>' +
      '</div>' + CLOSE_BTN + '</div>' +
      html +
      '<p class="cv-note">Not sure of your trim? It\'s usually on the tailgate badge or your registration. Or ' + finderLink('open the full Fit Finder') + '.</p>' +
      footer()
    );
  }

  function renderNone() {
    var v = ctx.vehicle;
    track('none', { sku: ctx.sku, make: v.make, model: v.model, year: v.year });
    render(
      '<div class="cv-head"><div class="cv-head__text">' + eyebrow('No match yet') +
        '<h2 id="cv-title" class="cv-title">We couldn\'t find an adapter for your ' + esc(vehicleName(v)) + '</h2>' +
        '<p class="cv-sub">' + finderLink('Open the full Fit Finder') + ' to see other ways to power your dash cam.</p>' +
      '</div>' + CLOSE_BTN + '</div>' +
      footer()
    );
  }

  function renderError() {
    render(
      '<div class="cv-head"><div class="cv-head__text">' +
        '<h2 id="cv-title" class="cv-title">Something went wrong</h2>' +
        '<p class="cv-sub">We couldn\'t check that vehicle. Please try again.</p>' +
      '</div>' + CLOSE_BTN + '</div>' +
      '<button type="button" class="cv-btn" data-cv="retry">Try again</button>'
    );
  }

  // ---------- swap ----------

  function swap(newSku, btn) {
    var p = ctx.products[newSku];
    if (!p || !ctx.key) return;
    var v = Object.assign({}, ctx.vehicle, { trim: ctx.trim || '' });
    var oldSku = ctx.sku;
    btn.disabled = true;
    btn.textContent = 'Swapping…';

    // Keep any line properties (e.g. the assistant's attribution) on the new line.
    nativeFetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var line = (cart.items || []).filter(function (i) { return i.key === ctx.key; })[0];
        if (!line) throw new Error('line gone');
        var props = line.properties || {};
        var oldVariant = line.variant_id;
        var qty = line.quantity;
        // Remove first so cable-check sees the same number of adapters.
        return nativeFetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: ctx.key, quantity: 0 })
        }).then(function (r) {
          if (!r.ok) throw new Error('remove failed');
          remember(newSku, v);
          close();
          // window.fetch so the pre-order and cable checks still run on the add.
          return window.fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: p.variantId, quantity: qty, properties: props }] })
          }).then(function (r) {
            if (!r.ok) throw new Error('add failed');
            track('swap', { from: oldSku, to: newSku, make: v.make, model: v.model, year: v.year, trim: v.trim });
          }).catch(function (err) {
            // Put the original adapter back.
            return nativeFetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: oldVariant, quantity: qty, properties: props }] })
            }).then(function () { throw err; });
          });
        });
      })
      .then(refreshDrawer, function (err) {
        console.warn('[cart-vehicle]', err);
        refreshDrawer();
        if (dlg && dlg.open) {
          btn.disabled = false;
          btn.textContent = 'Swap';
        }
      });
  }

  function refreshDrawer() {
    var drawer = document.querySelector('cart-drawer');
    return nativeFetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents({ sections: sections });
        } else {
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble && sections['cart-icon-bubble']) {
            bubble.innerHTML = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html')
              .querySelector('.shopify-section').innerHTML;
          }
        }
        scheduleApply();
      })
      .catch(function () {});
  }
})();
